/**
 * Database Bridge: Main thread ↔ db-worker.
 * Sends requests via postMessage and resolves promises on response.
 */

import type {
  WorkerRequest,
  WorkerResponse,
  InitPayload,
  ExecutePayload,
  RunPayload,
  QueryPayload,
  BatchPayload,
  BulkUpsertOfflineEntitiesPayload,
  RunResult,
  DbInfo,
  RestorePayload,
  DbExport,
  ImportDataPayload,
  FullResetPayload,
} from './types';
import { isIOS, isIOSPWA, isStandalone } from '../../utils/pwa-environment';
import { telemetryService } from '../../services/offline/telemetry/telemetry-service';

type WorkerRequestType = WorkerRequest['type'];

const _pending = new Map<string, { resolve: (v: unknown) => void; reject: (e: Error) => void }>();

function _nextId(): string {
  return `req_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

let _worker: Worker | null = null;
let _dbName = '';

function _rejectAllPending(reason: string): void {
  const error = new Error(reason);
  _pending.forEach(({ reject }) => reject(error));
  _pending.clear();
}

function _isIdbInitConflictError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? '');
  const normalized = message.toLowerCase();
  return (
    normalized.includes('createobjectstore') ||
    normalized.includes('object store with the specified name already exists') ||
    normalized.includes('upgradeneeded event handler') ||
    normalized.includes('version change transaction was aborted')
  );
}

async function _deleteIdbVfsDatabase(dbName: string): Promise<void> {
  const idbName = `idb-${dbName}`;
  await new Promise<void>((resolve, reject) => {
    const req = indexedDB.deleteDatabase(idbName);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error ?? new Error('Failed to delete IndexedDB database'));
    req.onblocked = () => {
      // Close any stale worker instance that might hold a connection lock.
      if (_worker) {
        _worker.terminate();
        _worker = null;
      }
      _rejectAllPending('Database worker restarted (IDB delete blocked)');
      resolve();
    };
  });
}

function _createWorker(): Worker {
  const w = new Worker(new URL('./db-worker.ts', import.meta.url), { type: 'module' });
  w.onmessage = (event: MessageEvent<WorkerResponse>) => {
    const res = event.data;

    if (res.id === '__vfs_degraded__') {
      window.dispatchEvent(new CustomEvent('db:vfs-degraded', { detail: res.data }));
      return;
    }
    if (res.id === '__vfs_strategy__') {
      window.dispatchEvent(new CustomEvent('db:vfs-strategy', { detail: res.data }));
      return;
    }

    const pending = _pending.get(res.id);
    if (!pending) return;
    _pending.delete(res.id);
    if (res.success) {
      pending.resolve(res.data);
    } else {
      pending.reject(new Error(res.error ?? 'Unknown error'));
    }
  };
  w.onerror = (event: ErrorEvent) => {
    telemetryService.trackError(new Error('db_worker_crashed'), {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      message: event.message,
    });
    telemetryService.trackWorkerHealth('crash', {
      reason: 'worker_onerror',
      message: event.message,
    });
    try {
      window.dispatchEvent(
        new CustomEvent('db:worker-crashed', {
          detail: { reason: 'worker_onerror', message: event.message },
        })
      );
    } catch {
      // ignore
    }
    _worker = null;
    _rejectAllPending('Database worker crashed');
  };
  w.onmessageerror = (event: MessageEvent) => {
    telemetryService.trackError(new Error('db_worker_message_error'), {
      dataType: typeof event.data,
    });
    telemetryService.trackWorkerHealth('crash', {
      reason: 'worker_onmessageerror',
    });
    try {
      window.dispatchEvent(
        new CustomEvent('db:worker-crashed', {
          detail: { reason: 'worker_onmessageerror' },
        })
      );
    } catch {
      // ignore
    }
    _worker = null;
    _rejectAllPending('Database worker message error');
  };
  return w;
}

function _getWorker(): Worker {
  if (_worker) return _worker;
  _worker = _createWorker();
  return _worker;
}

async function _sendRequest<T>(type: WorkerRequestType, payload?: unknown): Promise<T> {
  const id = _nextId();
  return new Promise<T>((resolve, reject) => {
    _pending.set(id, { resolve: resolve as (v: unknown) => void, reject });
    _getWorker().postMessage({ id, type, payload } as WorkerRequest);
  });
}

export class DatabaseBridge {
  private _initialized = false;
  private _initPromise: Promise<void> | null = null;
  private _transactionLock: Promise<void> = Promise.resolve();
  private _inTransaction = false;

  get isInitialized(): boolean {
    return this._initialized;
  }

  async initialize(dbName: string): Promise<void> {
    if (this._initialized) return;
    if (this._initPromise) {
      await this._initPromise;
      return;
    }
    _dbName = dbName;
    const payload: InitPayload = {
      dbName,
      platform: {
        isIOS: isIOS(),
        isIOSPWA: isIOSPWA(),
        isStandalone: isStandalone(),
      },
    };
    const promise = (async () => {
      try {
        await _sendRequest('init', payload);
        this._initialized = true;
      } catch (error) {
        if (!_isIdbInitConflictError(error)) {
          throw error;
        }

        // IndexedDB schema conflict (createObjectStore already exists / aborted
        // versionchange). Restart worker and retry once with a clean IDB VFS DB.
        if (_worker) {
          _worker.terminate();
          _worker = null;
        }
        _rejectAllPending('Database worker restarted after IDB schema conflict');

        try {
          await _deleteIdbVfsDatabase(dbName);
        } catch {
          // If direct IDB delete fails, fallback to worker-side reset.
          try {
            await _sendRequest<void>('fullReset', { dbName } satisfies FullResetPayload);
          } catch {
            // Ignore reset failures; retrying init is still useful in most cases.
          }
        }

        await _sendRequest('init', payload);
        this._initialized = true;
      }
    })();
    this._initPromise = promise;
    try {
      await promise;
    } finally {
      if (this._initPromise === promise) {
        this._initPromise = null;
      }
    }
  }

  /**
   * Terminate the crashed worker, reject all pending requests, and reinitialize.
   * Called automatically by WorkerWatchdog when the worker stops responding.
   */
  async terminateAndRestart(): Promise<void> {
    if (_worker) {
      _worker.terminate();
      _worker = null;
    }
    // Reject all in-flight requests so callers don't hang indefinitely.
    _pending.forEach(({ reject }) => reject(new Error('Worker restarted')));
    _pending.clear();
    this._initialized = false;
    this._initPromise = null;
    if (_dbName) {
      await this.initialize(_dbName);
    }
  }

  /**
   * Termina il worker senza reinizializzare. Utile prima di operazioni distruttive
   * (clear PWA / deleteDatabase) o se `close`/`fullReset` restano appesi; il reload ricrea tutto.
   */
  forceKillWorker(): void {
    if (_worker) {
      _worker.terminate();
      _worker = null;
    }
    _rejectAllPending('Database worker force-terminated');
    this._initialized = false;
    this._initPromise = null;
  }

  /**
   * Release SQLite's internal page cache back to the WASM heap.
   * Safe to call periodically (e.g. every health-check cycle).
   * Not a substitute for closing the DB — just frees idle cached pages.
   */
  async shrinkMemory(): Promise<void> {
    if (!this._initialized) return;
    try {
      await _sendRequest('execute', { sql: 'PRAGMA shrink_memory' } satisfies ExecutePayload);
    } catch {
      // Non-fatal: worker might be mid-restart.
    }
  }

  /**
   * Paginated query — wraps the SQL with LIMIT/OFFSET so large tables are
   * never fully materialised in memory.  Combine with react-virtuoso for
   * seamless infinite-scroll lists.
   *
   * @param sql      Base SQL (must NOT already contain LIMIT/OFFSET)
   * @param params   Bind parameters for the base SQL
   * @param page     Zero-based page index
   * @param pageSize Rows per page (default 100)
   */
  async queryPage<T = Record<string, unknown>>(
    sql: string,
    params: unknown[] = [],
    page = 0,
    pageSize = 100
  ): Promise<T[]> {
    this._ensureInitialized();
    const paginated = `${sql} LIMIT ? OFFSET ?`;
    return _sendRequest<T[]>('query', {
      sql: paginated,
      params: [...params, pageSize, page * pageSize],
    } satisfies QueryPayload);
  }

  async execute(sql: string): Promise<void> {
    this._ensureInitialized();
    await _sendRequest('execute', { sql } satisfies ExecutePayload);
  }

  async run(sql: string, params: unknown[] = []): Promise<RunResult> {
    this._ensureInitialized();
    return _sendRequest<RunResult>('run', { sql, params } satisfies RunPayload);
  }

  async query<T = Record<string, unknown>>(sql: string, params: unknown[] = []): Promise<T[]> {
    this._ensureInitialized();
    return _sendRequest<T[]>('query', { sql, params } satisfies QueryPayload);
  }

  async batch(
    statements: Array<{ sql: string; params: unknown[] }>,
    options?: { useTransaction?: boolean }
  ): Promise<void> {
    this._ensureInitialized();
    await _sendRequest('batch', { statements, useTransaction: options?.useTransaction } satisfies BatchPayload);
  }

  async bulkUpsertOfflineEntities(payload: BulkUpsertOfflineEntitiesPayload): Promise<void> {
    this._ensureInitialized();
    await _sendRequest<void>('bulkUpsertOfflineEntities', payload);
  }

  async transaction<T>(callback: () => Promise<T>): Promise<T> {
    this._ensureInitialized();

    // Serialize transactions: wait for any previous transaction to complete
    const prevLock = this._transactionLock;
    let releaseLock: () => void = () => {};
    this._transactionLock = new Promise<void>((resolve) => {
      releaseLock = resolve;
    });

    await prevLock;

    if (this._inTransaction) {
      releaseLock();
      throw new Error('Nested transactions are not supported');
    }

    this._inTransaction = true;
    try {
      await this.execute('BEGIN TRANSACTION');
      try {
        const result = await callback();
        await this.execute('COMMIT');
        return result;
      } catch (error) {
        try {
          await this.execute('ROLLBACK');
        } catch {
          // Ignore rollback errors if transaction already failed
        }
        throw error;
      }
    } finally {
      this._inTransaction = false;
      releaseLock();
    }
  }

  async close(): Promise<void> {
    if (!this._initialized) return;
    await _sendRequest('close');
    this._initialized = false;
    this._initPromise = null;
  }

  async export(): Promise<DbExport> {
    this._ensureInitialized();
    return _sendRequest<DbExport>('export');
  }

  async getInfo(): Promise<DbInfo> {
    this._ensureInitialized();
    return _sendRequest<DbInfo>('getInfo');
  }

  async restore(exportData: DbExport): Promise<void> {
    this._ensureInitialized();
    await _sendRequest<void>('restore', { resetOnly: true } satisfies RestorePayload);
    const { schemaRegistry } = await import('../schema-registry');
    await schemaRegistry.synchronize();
    await _sendRequest<void>('importData', { export: exportData } satisfies ImportDataPayload);
  }

  async fullReset(dbName = 'b2b_offline'): Promise<void> {
    await _sendRequest<void>('fullReset', { dbName } satisfies FullResetPayload);
    this._initialized = false;
    this._initPromise = null;
  }

  private _ensureInitialized(): void {
    if (!this._initialized) {
      throw new Error('Database not initialized. Call initialize() first.');
    }
  }
}

export const dbBridge = new DatabaseBridge();
